// Your Venues — full-screen list of venues you've actually been to.
// Per direction: aggregate ONLY from your own history (/api/me/nights).
// Tap a card → expand inline (caret rotates) showing per-night damage with
// drink-type emoji. Tap a night row → /night/:id/recap.
//
// Derived client-side because there's no /api/me/venues endpoint yet.

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { type NightDetailsDto, authApi, meApi } from '../api'
import { Icon } from '../components/Icon'
import { BackButton, TopBar } from '../components/TopBar'

export const Route = createFileRoute('/your-venues')({
  component: YourVenuesRoute,
})

const VENUE_COLORS = [
  'var(--pink)',
  'var(--orange)',
  'var(--teal)',
  'var(--lime)',
  'var(--plum)',
  'var(--acid)',
] as const

function venueColor(seed: string): string {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0
  return VENUE_COLORS[h % VENUE_COLORS.length] ?? VENUE_COLORS[0]
}

const DRINK_EMOJI: Record<string, string> = {
  beer: '🍺',
  wine: '🍷',
  cocktail: '🍹',
  shot: '🥃',
  other: '🍸',
}

function drinkEmoji(type: string): string {
  return DRINK_EMOJI[type.toLowerCase()] ?? '🥃'
}

type VenueNight = {
  nightId: string
  startedAt: Date
  dateLabel: string
  drinks: number
  drinkTypes: string[]
}

type VenueAgg = {
  venueId: string
  name: string
  city: string | null
  color: string
  // Server-proxied Google Places photo URL (NULL for local / no-photo venues).
  // /api/venues/:id/photo lazy-fetches + caches in R2 — safe to drop in <img>.
  photoUrl: string | null
  visits: number // distinct nights at this venue
  totalDrinks: number
  nights: VenueNight[]
}

type Status = { kind: 'loading' } | { kind: 'empty' } | { kind: 'ready'; venues: VenueAgg[] }

function YourVenuesRoute() {
  const navigate = useNavigate()
  const [state, setState] = useState<Status>({ kind: 'loading' })
  const [openId, setOpenId] = useState<string | null>(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: navigate is stable; we run once on mount.
  useEffect(() => {
    void (async () => {
      const me = await authApi.me()
      if (!me) {
        navigate({ to: '/login' })
        return
      }
      const nights = await meApi.history()
      const venues = aggregateVenues(nights)
      setState(venues.length === 0 ? { kind: 'empty' } : { kind: 'ready', venues })
    })()
  }, [])

  const count = useMemo(() => (state.kind === 'ready' ? state.venues.length : 0), [state])

  return (
    <div className="stumble-screen">
      <TopBar
        left={<BackButton onClick={() => navigate({ to: '/you' })} />}
        eyebrow={
          state.kind === 'ready' ? `${count} bar${count === 1 ? '' : 's'} · all yours` : 'loading…'
        }
        title="your venues"
      />
      <div className="screen-body with-nav" data-scroll="true" style={{ display: 'grid', gap: 10 }}>
        {state.kind === 'loading' && (
          <div className="card body-sm muted" style={{ textAlign: 'center' }}>
            loading your venues…
          </div>
        )}

        {state.kind === 'empty' && (
          <div
            className="card"
            style={{ textAlign: 'center', padding: 22, display: 'grid', gap: 6 }}
          >
            <p className="display" style={{ fontSize: 30, margin: 0 }}>
              no venues yet.
            </p>
            <p className="body-sm muted" style={{ margin: 0 }}>
              Check in tonight and they'll show up here.
            </p>
          </div>
        )}

        {state.kind === 'ready' &&
          state.venues.map((v) => {
            const open = openId === v.venueId
            return (
              <div
                key={v.venueId}
                className="venue-row"
                data-testid={`venue-row-${v.venueId}`}
                data-open={open ? 'true' : 'false'}
              >
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : v.venueId)}
                  aria-expanded={open}
                  aria-controls={`venue-detail-${v.venueId}`}
                  data-testid={`venue-toggle-${v.venueId}`}
                  className="venue-row-head"
                >
                  <span className="venue-row-thumb" style={{ background: v.color }} aria-hidden>
                    {v.photoUrl && (
                      <img
                        src={v.photoUrl}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          // Hide on 404 (no_photo / not_found) — color fill
                          // remains as the visual fallback.
                          ;(e.currentTarget as HTMLImageElement).style.display = 'none'
                        }}
                      />
                    )}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontFamily: 'var(--font-display)',
                        fontSize: 22,
                        lineHeight: 1,
                        textTransform: 'lowercase',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {v.name}
                    </div>
                    <div className="row" style={{ gap: 6, marginTop: 6 }}>
                      <span
                        className="chip"
                        style={{
                          background: v.color,
                          color: 'var(--paper)',
                          padding: '3px 8px',
                          fontSize: 10,
                        }}
                      >
                        {v.visits} night{v.visits === 1 ? '' : 's'}
                      </span>
                      <span className="chip" style={{ padding: '3px 8px', fontSize: 10 }}>
                        {v.totalDrinks} drink{v.totalDrinks === 1 ? '' : 's'}
                      </span>
                      {v.city && (
                        <span
                          className="chip"
                          style={{ padding: '3px 8px', fontSize: 10, opacity: 0.85 }}
                        >
                          {v.city}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="venue-row-caret" data-open={open ? 'true' : 'false'}>
                    <Icon name="back" size={14} />
                  </span>
                </button>

                {open && (
                  <div
                    id={`venue-detail-${v.venueId}`}
                    className="venue-row-detail"
                    data-testid={`venue-detail-${v.venueId}`}
                  >
                    <div className="eyebrow" style={{ paddingTop: 8, fontSize: 10 }}>
                      nights here
                    </div>
                    {v.nights.map((n) => (
                      <button
                        type="button"
                        key={n.nightId}
                        onClick={() =>
                          navigate({ to: '/night/$id/recap', params: { id: n.nightId } })
                        }
                        className="venue-night-row"
                        data-testid={`venue-night-${n.nightId}`}
                      >
                        <span className="venue-night-count" style={{ background: v.color }}>
                          {n.drinks}
                        </span>
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontFamily: 'var(--font-display)',
                              fontSize: 17,
                              lineHeight: 1,
                              textTransform: 'lowercase',
                            }}
                          >
                            {n.dateLabel}
                          </div>
                          {n.drinkTypes.length > 0 && (
                            <div style={{ display: 'flex', gap: 2, marginTop: 4 }}>
                              {n.drinkTypes.slice(0, 10).map((t, ti) => (
                                <span key={`${n.nightId}-${ti}`} style={{ fontSize: 14 }}>
                                  {drinkEmoji(t)}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <span className="venue-night-arrow" aria-hidden>
                          <Icon name="back" size={14} />
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
      </div>

      <style>{`
        .venue-row {
          border: 2.5px solid var(--line);
          border-radius: 16px;
          background: var(--paper);
          box-shadow: 0 5px 0 var(--shadow-ink);
          overflow: hidden;
        }
        .venue-row-head {
          display: grid;
          grid-template-columns: 56px 1fr auto;
          gap: 12px;
          padding: 12px 14px;
          width: 100%;
          border: none;
          background: transparent;
          text-align: left;
          align-items: center;
          color: var(--ink);
        }
        .venue-row-thumb {
          width: 56px;
          height: 56px;
          border-radius: 12px;
          border: 2px solid var(--line);
          overflow: hidden;
          display: block;
          position: relative;
        }
        .venue-row-thumb img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .venue-row-caret {
          width: 32px;
          height: 32px;
          border-radius: 999px;
          border: 2px solid var(--line);
          background: var(--paper-warm);
          display: grid;
          place-items: center;
          color: var(--ink);
          transform: rotate(-90deg);
          transition: transform 220ms;
        }
        .venue-row-caret[data-open="true"] { transform: rotate(90deg); }
        .venue-row-detail {
          padding: 0 14px 12px;
          border-top: 1.5px dashed var(--line);
          display: grid;
          gap: 8px;
          margin-top: 4px;
        }
        .venue-night-row {
          display: grid;
          grid-template-columns: auto 1fr auto;
          gap: 10px;
          align-items: center;
          padding: 8px 10px;
          border-radius: 10px;
          background: var(--paper-warm);
          border: 2px solid var(--line);
          color: var(--ink);
          text-align: left;
          width: 100%;
        }
        .venue-night-row:active { transform: translateY(2px); }
        .venue-night-count {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          color: var(--paper);
          display: grid;
          place-items: center;
          font-family: var(--font-display);
          font-size: 18px;
          border: 2px solid var(--line);
        }
        .venue-night-arrow {
          width: 32px;
          height: 32px;
          border-radius: 999px;
          border: 2px solid var(--line);
          background: var(--paper);
          display: grid;
          place-items: center;
          color: var(--ink);
          transform: scaleX(-1);
        }
      `}</style>
    </div>
  )
}

function aggregateVenues(nights: NightDetailsDto[]): VenueAgg[] {
  // Map venueId → aggregate. We only consider venues for which we have a
  // SessionVenue (i.e. actual check-ins) — totals.byVenue is the right source.
  const byVenue = new Map<
    string,
    {
      name: string
      city: string | null
      photoUrl: string | null
      nights: VenueNight[]
      visitNightIds: Set<string>
      totalDrinks: number
    }
  >()

  for (const detail of nights) {
    const startedAt = new Date(detail.night.startedAt)
    const dateLabel = startedAt
      .toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      })
      .toLowerCase()

    // For each venue this night touched, gather drink count + types.
    const venueDrinkTypes = new Map<string, string[]>()
    for (const d of detail.drinks) {
      const v = detail.sessionVenues.find((sv) => sv.id === d.sessionVenueId)
      const venueId = v?.venueId
      if (!venueId) continue
      const arr = venueDrinkTypes.get(venueId)
      if (arr) arr.push(d.drinkType)
      else venueDrinkTypes.set(venueId, [d.drinkType])
    }

    for (const sv of detail.sessionVenues) {
      const venueId = sv.venueId
      const venueName = sv.venue.name
      const venueCity = sv.venue.city
      const venuePhotoUrl = sv.venue.photoUrl ?? null
      const drinkTypes = venueDrinkTypes.get(venueId) ?? []
      const drinks = drinkTypes.length

      let entry = byVenue.get(venueId)
      if (!entry) {
        entry = {
          name: venueName,
          city: venueCity,
          photoUrl: venuePhotoUrl,
          nights: [],
          visitNightIds: new Set(),
          totalDrinks: 0,
        }
        byVenue.set(venueId, entry)
      } else if (!entry.photoUrl && venuePhotoUrl) {
        // Older detail rows might lack photoUrl (e.g. pre-migration cached
        // responses) — first non-null wins.
        entry.photoUrl = venuePhotoUrl
      }
      if (!entry.visitNightIds.has(detail.night.id)) {
        entry.visitNightIds.add(detail.night.id)
        entry.nights.push({
          nightId: detail.night.id,
          startedAt,
          dateLabel,
          drinks,
          drinkTypes,
        })
        entry.totalDrinks += drinks
      }
    }
  }

  const out: VenueAgg[] = []
  for (const [venueId, agg] of byVenue) {
    // Most recent night first within each venue.
    agg.nights.sort((a, b) => b.startedAt.getTime() - a.startedAt.getTime())
    out.push({
      venueId,
      name: agg.name,
      city: agg.city,
      color: venueColor(venueId),
      photoUrl: agg.photoUrl,
      visits: agg.visitNightIds.size,
      totalDrinks: agg.totalDrinks,
      nights: agg.nights,
    })
  }
  // Most-visited venues first, then highest damage as tiebreaker.
  out.sort((a, b) => b.visits - a.visits || b.totalDrinks - a.totalDrinks)
  return out
}
